import React from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  Collapse,
  IconButton,
  LinearProgress,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HealthAndSafetyOutlinedIcon from "@mui/icons-material/HealthAndSafetyOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import {
  fetchOperatorConsole,
  type OperatorConsoleSummary,
} from "../../services/operatorConsoleService";
import { useCoordinatedRefresh } from "../../hooks/useCoordinatedRefresh";

const money = (value: number) => `$${value.toFixed(value < 0.01 ? 4 : 2)}`;
const relative = (iso: string | null) => {
  if (!iso) return "No event in the last 7 days";
  const minutes = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (minutes < 2) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  return hours < 48 ? `${hours}h ago` : `${Math.round(hours / 24)}d ago`;
};

const SpendLine: React.FC<{
  label: string;
  today: number;
  week: number;
  cap: number;
  source: string;
  window: string;
  basis?: string;
  through: string;
}> = ({ label, today, week, cap, source, window, basis, through }) => {
  const percent = cap > 0 ? Math.min(100, (today / cap) * 100) : 0;
  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" gap={1}>
        <Typography variant="body2" fontWeight={700}>{label}</Typography>
        <Typography variant="body2" fontWeight={800}>{money(today)} today</Typography>
      </Stack>
      <LinearProgress variant="determinate" value={percent} sx={{ my: 0.75, height: 6, borderRadius: 3 }} />
      <Tooltip title={`${source} · ${window} · through ${new Date(through).toLocaleString()}${basis ? ` · ${basis}` : ""}`} arrow>
        <Typography variant="caption" color="text.secondary" tabIndex={0} sx={{ cursor: "help" }}>
          {money(week)} / 7 days · {Math.round(percent)}% of {money(cap)} cap
        </Typography>
      </Tooltip>
    </Box>
  );
};

const OperatorConsoleRail: React.FC = () => {
  const theme = useTheme();
  const [summary, setSummary] = React.useState<OperatorConsoleSummary | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(false);
  const [detailsOpen, setDetailsOpen] = React.useState(false);

  const mountedRef = React.useRef(true);
  React.useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);
  const load = React.useCallback(async () => {
    try {
      const result = await fetchOperatorConsole();
      if (mountedRef.current) { setSummary(result); setError(false); }
    } catch (err) {
      console.warn("Operator console telemetry unavailable", err);
      if (mountedRef.current) setError(true);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);
  useCoordinatedRefresh(load, { pollMs: 60_000 });

  if (loading && !summary) {
    return <Stack spacing={2} mb={3}><Skeleton variant="rounded" height={220} /><Skeleton variant="rounded" height={220} /></Stack>;
  }
  if (!summary) {
    return <Alert severity="warning" sx={{ mb: 3 }}>Machine telemetry is reconnecting. Lead work remains available.</Alert>;
  }

  const channel = summary.health.director_channel;
  const generated = relative(summary.generated_at);
  return (
    <Stack spacing={2.5} sx={{ mb: 3 }}>
      {error && <Alert severity="info">Showing the last successful machine snapshot · reconnecting…</Alert>}
      <Card>
        <CardContent>
          <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1} mb={1.5}>
            <Box>
              <Typography variant="h6" fontWeight={700}>Shortlist next actions</Typography>
              <Typography variant="caption" color="text.secondary">Verified renter outcomes · drafts stay held for review</Typography>
            </Box>
            <Chip size="small" label={summary.shortlist_next_actions.length} color={summary.shortlist_next_actions.length ? "warning" : "default"} />
          </Stack>
          {summary.shortlist_next_actions.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No shortlist follow-up is waiting.</Typography>
          ) : (
            <Stack spacing={1}>
              {summary.shortlist_next_actions.map((action) => (
                <Box key={action.id} sx={{ p: 1.25, border: "1px solid", borderColor: "divider", borderRadius: 1.5 }}>
                  <Stack direction="row" gap={1} justifyContent="space-between" alignItems="flex-start">
                    <Box minWidth={0}>
                      <Typography variant="body2" fontWeight={750}>{action.title}</Typography>
                      <Typography variant="caption" color="text.secondary">{action.description || "Review this verified shortlist outcome."}</Typography>
                    </Box>
                    <Chip size="small" label={action.priority} color={action.priority === "urgent" ? "error" : "warning"} sx={{ textTransform: "capitalize" }} />
                  </Stack>
                </Box>
              ))}
            </Stack>
          )}
        </CardContent>
      </Card>
      <Card>
        <CardContent>
          <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1} mb={1.5}>
            <Stack direction="row" alignItems="center" gap={1}>
              <AutoAwesomeIcon color="primary" />
              <Box>
                <Typography variant="h6" fontWeight={700}>Machine-assisted work</Typography>
                <Typography variant="caption" color="text.secondary">Operator-triggered outcomes from the text-command audit</Typography>
              </Box>
            </Stack>
            <Chip size="small" label={`Updated ${generated}`} />
          </Stack>
          {summary.operator_triggered_events.length === 0 ? (
            <Typography variant="body2" color="text.secondary" py={2}>No verified operator-triggered outcomes in the last 7 days.</Typography>
          ) : (
            <Stack spacing={0.5}>
              {summary.operator_triggered_events.map((event) => {
                const failed = event.status !== "executed";
                const statusLabel = event.status === "executed" ? "Completed" : event.status === "failed" ? "Failed" : event.status === "cancelled" ? "Cancelled" : "Rate limited";
                return (
                  <Box key={event.id} sx={{ display: "grid", gridTemplateColumns: "24px 1fr auto", gap: 1, py: 1, borderBottom: `1px solid ${theme.palette.divider}`, "&:last-child": { border: 0 } }}>
                    {failed ? <ErrorOutlineIcon color="warning" fontSize="small" /> : <CheckCircleOutlineIcon color="success" fontSize="small" />}
                    <Box minWidth={0}>
                      <Typography variant="body2" fontWeight={700}>{event.label} · {statusLabel}</Typography>
                      <Typography variant="caption" color="text.secondary">Operator-triggered · {event.source}</Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary" whiteSpace="nowrap">{relative(event.occurred_at)}</Typography>
                  </Box>
                );
              })}
            </Stack>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Stack direction="row" alignItems="center" gap={1} mb={2}>
            <HealthAndSafetyOutlinedIcon color={channel.state === "healthy" ? "success" : "disabled"} />
            <Box flex={1}>
              <Typography variant="h6" fontWeight={700}>Machine health</Typography>
              <Typography variant="caption" color="text.secondary">Director channel · {relative(channel.last_event_at)}</Typography>
            </Box>
            <Chip size="small" color={channel.state === "healthy" ? "success" : "default"} label={channel.state === "healthy" ? "Reporting" : "No recent signal"} />
          </Stack>
          <Typography variant="caption" color="text.secondary">Health means a persisted command event exists; it does not prove every worker is healthy.</Typography>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Stack direction="row" alignItems="center" gap={1} mb={2}>
            <PaymentsOutlinedIcon color="primary" />
            <Box flex={1}>
              <Typography variant="h6" fontWeight={700}>System cost controls</Typography>
              <Typography variant="caption" color="text.secondary">Recorded NLU cost and conservative verification cap accounting</Typography>
            </Box>
            <IconButton size="small" aria-label="Show spend formulas" aria-expanded={detailsOpen} onClick={() => setDetailsOpen((v) => !v)}>
              <ExpandMoreIcon sx={{ transform: detailsOpen ? "rotate(180deg)" : "none", transition: "transform 150ms" }} />
            </IconButton>
          </Stack>
          <Stack spacing={2}>
            <SpendLine label="Anthropic · text-command NLU" today={summary.spend.anthropic_nlu.today_usd} week={summary.spend.anthropic_nlu.seven_day_usd} cap={summary.spend.anthropic_nlu.daily_cap_usd} source={summary.spend.anthropic_nlu.source} window={summary.spend.anthropic_nlu.window} through={summary.spend.anthropic_nlu.through} />
            <SpendLine label="Lead verification · estimated" today={summary.spend.twilio_lookup.today_usd} week={summary.spend.twilio_lookup.seven_day_usd} cap={summary.spend.twilio_lookup.daily_cap_usd} source={summary.spend.twilio_lookup.source} window={summary.spend.twilio_lookup.window} through={summary.spend.twilio_lookup.through} basis={summary.spend.twilio_lookup.pricing_basis} />
          </Stack>
          <Collapse in={detailsOpen}>
            <Box sx={{ mt: 2, p: 1.5, borderRadius: 2, bgcolor: alpha(theme.palette.warning.main, 0.08) }}>
              <Typography variant="body2" fontWeight={700}>Twilio messaging billing: not connected</Typography>
              <Typography variant="caption" color="text.secondary">{summary.spend.twilio_messaging.reason} Verification is shown separately and is not labeled as total Twilio spend.</Typography>
            </Box>
          </Collapse>
        </CardContent>
      </Card>
    </Stack>
  );
};

export default OperatorConsoleRail;
